import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";

/** Unwrap MongoDB $numberDecimal or plain values to string. */
export function decStr(v) {
    if (v == null) return "";
    if (typeof v === "object" && v.$numberDecimal != null) return String(v.$numberDecimal);
    return String(v);
}

export function decNum(v) {
    const n = parseFloat(decStr(v));
    return Number.isFinite(n) ? n : 0;
}

/** BTC → BTCUSDT */
export function underlyingKeyFromAsset(asset) {
    const base = String(asset || "").trim().toUpperCase();
    if (!base) return "";
    if (base.endsWith("USDT") || base.endsWith("USDC")) return base;
    return `${base}USDT`;
}

/** BTCUSDT → BTC */
export function assetFromUnderlying(underlying) {
    return String(underlying || "").replace(/USDT$|USDC$/i, "");
}

/** Parse expiry date from symbol e.g. BTC-260925-100000-C → 2026-09-25 */
export function parseExpiryFromSymbol(symbol) {
    const m = String(symbol || "").match(/^[A-Z0-9]+-(\d{6})-/i);
    if (!m) return null;
    const s = m[1];
    return `20${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4, 6)}`;
}

export function parseStrikeFromSymbol(symbol) {
    const m = String(symbol || "").match(/^[A-Z0-9]+-\d{6}-(\d+)-[CP]$/i);
    return m ? decNum(m[1]) : 0;
}

export function fmtTs(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function mapContractLeg(c) {
    const bid = decNum(c.bid_price);
    const ask = decNum(c.ask_price);
    const mark = decNum(c.mark_price);
    const iv = decNum(c.iv);
    const ivPct = iv > 0 ? iv * 100 : 0;
    const spread = ask > 0 && bid > 0 ? ask - bid : 0;
    return {
        symbol: c.symbol,
        last: mark > 0 ? mark : null,
        vega: decNum(c.vega),
        theta: decNum(c.theta),
        gamma: decNum(c.gamma),
        delta: decNum(c.delta),
        bidSize: 0,
        bidIv: bid > 0 ? bid : null,
        markIv: mark > 0 ? mark : null,
        markIvPct: ivPct,
        iv,
        spread,
        spreadBp: mark > 0 ? (spread / mark) * 10000 : 0,
        askIv: ask > 0 ? ask : null,
        askIvPct: ivPct,
        askSize: 0,
    };
}

/**
 * Group flat contracts[] into chain rows per expiry for the options table UI.
 * @returns {Array<{ date, fwd, vol, data }>}
 */
export function buildChainsFromContracts(contracts, spotPrice) {
    if (!Array.isArray(contracts) || contracts.length === 0) return [];

    const byExpiry = new Map();
    for (const c of contracts) {
        const expiry = parseExpiryFromSymbol(c.symbol);
        if (!expiry) continue;
        if (!byExpiry.has(expiry)) byExpiry.set(expiry, []);
        byExpiry.get(expiry).push(c);
    }

    const spot = decNum(spotPrice);
    const chains = [];

    for (const [date, list] of [...byExpiry.entries()].sort()) {
        const byStrike = new Map();
        for (const c of list) {
            const strike = decNum(c.strike) || parseStrikeFromSymbol(c.symbol);
            if (!strike) continue;
            if (!byStrike.has(strike)) {
                byStrike.set(strike, { strike, call: null, put: null, callRaw: null, putRaw: null });
            }
            const row = byStrike.get(strike);
            const type = String(c.option_type || "").toUpperCase();
            if (type === "CALL") {
                row.callRaw = c;
                row.call = mapContractLeg(c);
            } else if (type === "PUT") {
                row.putRaw = c;
                row.put = mapContractLeg(c);
            }
        }

        const data = [...byStrike.values()]
            .sort((a, b) => a.strike - b.strike)
            .map(({ strike, call, put, callRaw, putRaw }) => {
                const diffPct = spot > 0 ? ((strike - spot) / spot) * 100 : 0;
                const emptyLeg = {
                    symbol: "",
                    last: null,
                    vega: 0,
                    theta: 0,
                    gamma: 0,
                    delta: 0,
                    bidSize: 0,
                    bidIv: 0,
                    markIv: 0,
                    markIvPct: 0,
                    spread: 0,
                    spreadBp: 0,
                    askIv: 0,
                    askIvPct: 0,
                    askSize: 0,
                };
                return {
                    strike,
                    diffPct,
                    call: call || emptyLeg,
                    put: put || emptyLeg,
                    callRaw,
                    putRaw,
                };
            });

        chains.push({
            date,
            dte: "",
            fwd: spot,
            vol: "—",
            data,
        });
    }

    return chains;
}

/** Find mapped call/put leg in chain rows by contract symbol. */
export function findContractLegInChains(chains, symbol) {
    if (!symbol || !Array.isArray(chains)) return null;
    for (const chain of chains) {
        for (const row of chain.data || []) {
            if (row.call?.symbol === symbol) return row.call;
            if (row.put?.symbol === symbol) return row.put;
        }
    }
    return null;
}

/** Build trade-panel selection object for a contract symbol from chain rows. */
export function buildSelectedOptionFromChains(chains, symbol) {
    if (!symbol || !Array.isArray(chains)) return null;
    for (const chain of chains) {
        for (const row of chain.data || []) {
            if (row.call?.symbol === symbol) {
                return {
                    strike: row.strike,
                    type: "call",
                    date: chain.date,
                    data: row.call,
                    symbol: row.call.symbol,
                    contractRaw: row.callRaw,
                };
            }
            if (row.put?.symbol === symbol) {
                return {
                    strike: row.strike,
                    type: "put",
                    date: chain.date,
                    data: row.put,
                    symbol: row.put.symbol,
                    contractRaw: row.putRaw,
                };
            }
        }
    }
    return null;
}

/** First call (else put) across expiries — used when URL contract is missing/unavailable. */
export function findFirstContractSelection(chains) {
    if (!Array.isArray(chains)) return null;
    for (const chain of chains) {
        for (const row of chain.data || []) {
            if (row.call?.symbol) {
                return {
                    strike: row.strike,
                    type: "call",
                    date: chain.date,
                    data: row.call,
                    symbol: row.call.symbol,
                    contractRaw: row.callRaw,
                };
            }
            if (row.put?.symbol) {
                return {
                    strike: row.strike,
                    type: "put",
                    date: chain.date,
                    data: row.put,
                    symbol: row.put.symbol,
                    contractRaw: row.putRaw,
                };
            }
        }
    }
    return null;
}

/** Raw contract row from WS/API (tick_size, step_size, status). */
export function findContractRawInChains(chains, symbol) {
    if (!symbol || !Array.isArray(chains)) return null;
    for (const chain of chains) {
        for (const row of chain.data || []) {
            if (row.callRaw?.symbol === symbol) return row.callRaw;
            if (row.putRaw?.symbol === symbol) return row.putRaw;
        }
    }
    return null;
}

/** Most recent trade price from contract_detail recent_trades_update. */
export function getLatestTradePrice(trades) {
    if (!Array.isArray(trades) || trades.length === 0) return null;
    let bestPrice = null;
    let bestTs = -Infinity;
    for (const t of trades) {
        const price = decNum(t.price);
        if (price <= 0) continue;
        let ts = decNum(t.timestamp);
        if (!Number.isFinite(ts) || ts <= 0) {
            const d = new Date(t.timestamp);
            ts = Number.isNaN(d.getTime()) ? 0 : d.getTime();
        }
        if (ts >= bestTs) {
            bestTs = ts;
            bestPrice = price;
        }
    }
    return bestPrice;
}

/** BTCUSDT → BTC/USDT for display */
export function formatUnderlyingPair(underlying) {
    const u = String(underlying || "");
    const m = u.match(/^(.+?)(USDT|USDC)$/i);
    if (m) return `${m[1]}/${m[2].toUpperCase()}`;
    return u || "—";
}

/** Resolve backend icon path from market_overview.underlying_icons */
export function resolveUnderlyingIconUrl(iconPath) {
    if (!iconPath) return "/images/coin_placeholder.png";
    const s = String(iconPath).trim();
    if (!s) return "/images/coin_placeholder.png";
    if (s.startsWith("http") || s.startsWith("/images/")) return s;
    return `${resolvePublicUploadUrl(s.replace(/^\//, ""))}`;
}

/** Build ticker list from market_overview payload. */
export function underlyingsFromMarketOverview(overview) {
    const list = overview?.underlyings || [];
    const prices = overview?.index_prices || {};
    const icons = overview?.underlying_icons || {};
    return list.map((u) => {
        const symbol = assetFromUnderlying(u);
        const price = decNum(prices[u]);
        return {
            symbol,
            underlying: u,
            price,
            iconPath: icons[u] || null,
            change: 0,
            isPos: true,
        };
    });
}

/** Parse strike filter input e.g. "30,000" → 30000 */
export function parseStrikeFilterInput(v) {
    const n = parseFloat(String(v ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** Smallest positive gap between sorted strikes in a chain group. */
function inferStandardStrikeStep(strikes) {
    if (!strikes.length) return null;
    const sorted = [...strikes].sort((a, b) => a - b);
    let minGap = null;
    for (let i = 1; i < sorted.length; i++) {
        const gap = sorted[i] - sorted[i - 1];
        if (gap > 0 && (minGap == null || gap < minGap)) minGap = gap;
    }
    return minGap;
}

function isOnStandardStrikeGrid(strike, anchor, step) {
    if (!step || step <= 0) return true;
    const offset = strike - anchor;
    const remainder = Math.abs(offset % step);
    return remainder < 0.01 || Math.abs(remainder - step) < 0.01;
}

/**
 * Client-side chain filters: strike range + odd size (non-standard strikes hidden when off).
 */
export function applyChainFilters(chains, { oddSize = true, strikeMin = null, strikeMax = null } = {}) {
    if (!Array.isArray(chains)) return [];

    return chains
        .map((chain) => {
            const strikes = (chain.data || []).map((r) => r.strike);
            const step = inferStandardStrikeStep(strikes);
            const anchor = strikes.length ? Math.min(...strikes) : 0;

            const data = (chain.data || []).filter((row) => {
                const { strike } = row;
                if (strikeMin != null && strike < strikeMin) return false;
                if (strikeMax != null && strike > strikeMax) return false;
                if (!oddSize && step != null && !isOnStandardStrikeGrid(strike, anchor, step)) {
                    return false;
                }
                return true;
            });

            return { ...chain, data };
        })
        .filter((chain) => chain.data.length > 0);
}

/** Options typically settle 08:00 UTC on expiry date (Binance-style). */
export function expiryTimestampMs(expiryDateStr) {
    if (!expiryDateStr) return null;
    const ms = new Date(`${expiryDateStr}T08:00:00.000Z`).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/** Expiry dates that sit in a consecutive daily series (±1 day neighbors). */
export function buildDailyExpirySet(expiryDates) {
    const sorted = [...(expiryDates || [])].sort();
    const daily = new Set();
    for (let i = 0; i < sorted.length; i++) {
        const cur = new Date(`${sorted[i]}T00:00:00.000Z`).getTime();
        if (i > 0) {
            const prev = new Date(`${sorted[i - 1]}T00:00:00.000Z`).getTime();
            if ((cur - prev) / 86400000 === 1) daily.add(sorted[i]);
        }
        if (i < sorted.length - 1) {
            const next = new Date(`${sorted[i + 1]}T00:00:00.000Z`).getTime();
            if ((next - cur) / 86400000 === 1) daily.add(sorted[i]);
        }
    }
    return daily;
}

/**
 * Time to expiration label e.g. "71d 6h (Monthly)", "2d 6h (Daily)", "06:43:08 (Daily)".
 * @param {number} [nowMs] — pass ticking clock for live countdown
 */
export function formatTimeToExpiration(expiryDateStr, dailyExpirySet, nowMs = Date.now()) {
    const expiryMs = expiryTimestampMs(expiryDateStr);
    if (expiryMs == null) return "—";

    const diff = Math.max(0, expiryMs - nowMs);
    const oneDay = 86400000;
    const isDaily = dailyExpirySet?.has?.(expiryDateStr) ?? false;
    const label = isDaily ? "Daily" : "Monthly";
    const pad = (n) => String(n).padStart(2, "0");

    if (isDaily && diff < oneDay) {
        const totalSec = Math.floor(diff / 1000);
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        return `${pad(h)}:${pad(m)}:${pad(s)} (${label})`;
    }

    const days = Math.floor(diff / oneDay);
    const hours = Math.floor((diff % oneDay) / 3600000);
    return `${days}d ${hours}h (${label})`;
}

/** Build cumulative depth rows for orderbook UI. */
export function buildOrderbookDisplayRows(levels) {
    if (!Array.isArray(levels) || levels.length === 0) return [];
    let cum = 0;
    const maxCum = levels.reduce((s, r) => s + decNum(r.qty), 0);
    return levels.map((row) => {
        cum += decNum(row.qty);
        const depth = maxCum > 0 ? Math.round((cum / maxCum) * 100) : 0;
        return {
            price: decNum(row.price),
            qty: decNum(row.qty),
            sum: cum,
            depth,
        };
    });
}

export function fmtTradeTimeShort(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Normalize orderbook_update bids/asks to [{ price, qty }] */
export function normalizeOrderbookLevels(levels) {
    if (!Array.isArray(levels)) return [];
    return levels.map((row) => {
        if (Array.isArray(row)) {
            return { price: decNum(row[0]), qty: decNum(row[1]) };
        }
        return {
            price: decNum(row.price ?? row[0]),
            qty: decNum(row.quantity ?? row.qty ?? row[1]),
        };
    });
}

/** Find contract row data for selected option in chains. */
export function findContractInChains(chains, { date, strike, type }) {
    const chain = chains.find((c) => c.date === date);
    if (!chain) return null;
    const row = chain.data.find((r) => r.strike === strike);
    if (!row) return null;
    const leg = type === "call" ? row.call : row.put;
    const raw = type === "call" ? row.callRaw : row.putRaw;
    return { leg, raw, row };
}

export function formatOptionsOrderType(orderType) {
    const t = String(orderType || "").toUpperCase().replace(/_/g, " ");
    return t || "—";
}

export function formatOptionsOrderStatus(status) {
    const s = String(status || "").toUpperCase();
    if (!s) return "—";
    return s.replace(/_/g, " ");
}

export function formatOptionsOrderIntent(intent) {
    const i = String(intent || "").toUpperCase();
    if (!i) return "—";
    return i.replace(/_/g, " ");
}

export function isReduceOnlyIntent(orderIntent) {
    const intent = String(orderIntent || "").toUpperCase();
    return intent.includes("CLOSE") || intent.includes("REDUCE");
}

export function formatOptionsOpenOrderPrice(item) {
    const type = String(item?.order_type || "").toUpperCase();
    if (type === "MARKET") return "Market";
    const price = decNum(item?.price);
    return price > 0 ? decStr(item.price) : "—";
}

export function optionsOpenOrderAvgFill(item) {
    const filled = decNum(item?.filled_quantity);
    if (filled <= 0) return "—";
    const avg = decNum(item?.avg_fill_price ?? item?.average_price ?? item?.fill_price);
    if (avg > 0) return decStr(item?.avg_fill_price ?? item?.average_price ?? item?.fill_price);
    return "—";
}

export function optionsOrderStatusClass(status) {
    const s = String(status || "").toUpperCase();
    if (s === "FILLED") return "agcex-spot-side-buy";
    if (s === "NEW" || s === "PARTIALLY_FILLED") return "";
    if (s === "LOCKED") return "text-muted";
    if (s === "CANCELLED" || s === "REJECTED" || s === "EXPIRED") return "agcex-spot-side-sell";
    return "";
}

export function optionsPositionSideClass(side) {
    const s = String(side || "").toUpperCase();
    if (s === "LONG") return "agcex-spot-side-buy";
    if (s === "SHORT") return "agcex-spot-side-sell";
    return "";
}

/** Closable size = position qty minus opposite-side open order remaining qty. */
export function computeOptionsPositionAvailable(position, openOrders = []) {
    const qty = decNum(position?.quantity);
    const side = String(position?.side || "").toUpperCase();
    const symbol = position?.symbol;
    if (!symbol || qty <= 0) return 0;

    let locked = 0;
    for (const o of openOrders) {
        if (o.symbol !== symbol) continue;
        const rem = decNum(o.remaining_quantity);
        const oSide = String(o.side || "").toUpperCase();
        if (side === "LONG" && oSide === "SELL") locked += rem;
        if (side === "SHORT" && oSide === "BUY") locked += rem;
    }
    return Math.max(0, qty - locked);
}

/** Mark × qty, or API position_notional when present. */
export function computeOptionsPositionMarketValue(position) {
    const notional = decNum(position?.position_notional);
    if (notional > 0) return notional;
    const mark = decNum(position?.mark_price);
    const qty = decNum(position?.quantity);
    if (mark > 0 && qty > 0) return mark * qty;
    return decNum(position?.open_notional);
}

export function optionsPositionPnlClass(pnl) {
    const n = decNum(pnl);
    if (!Number.isFinite(n) || n === 0) return "";
    return n > 0 ? "agce-ft-pnl--profit" : "agce-ft-pnl--loss";
}

export function formatOptionsPositionPnl(pnl, pnlPercent) {
    const n = decNum(pnl);
    const pct = decNum(pnlPercent);
    const sign = n > 0 ? "+" : "";
    const className = optionsPositionPnlClass(pnl);
    const absPnl = Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    });
    const pnlPart = n === 0 ? "0 USDT" : `${n < 0 ? "-" : sign}${absPnl} USDT`;
    const pctPart = Number.isFinite(pct) && pct !== 0
        ? ` (${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%)`
        : "";
    return { text: `${pnlPart}${pctPart}`, className };
}

export function optionsPositionSideAccent(side) {
    const s = String(side || "").toUpperCase();
    if (s === "LONG") return "#0ecb81";
    if (s === "SHORT") return "#f6465d";
    return "#848e9c";
}

/** Two-line symbol e.g. BTC-260619 / 59,000-Put */
export function formatOptionsPositionSymbolDisplay(symbol, row = {}) {
    const sym = String(symbol || "").trim();
    const m = sym.match(/^([A-Z0-9]+)-(\d{6})-(\d+)-([CP])$/i);
    if (m) {
        const [, base, expiryCode, strikeRaw, cp] = m;
        const typeLabel = cp.toUpperCase() === "C" ? "Call" : "Put";
        const strikeNum = decNum(strikeRaw);
        const strikeLabel = strikeNum > 0
            ? strikeNum.toLocaleString(undefined, { maximumFractionDigits: 3 })
            : strikeRaw;
        return {
            primary: `${base}-${expiryCode}`,
            secondary: `${strikeLabel}-${typeLabel}`,
        };
    }

    const optionType = String(row?.option_type || "").toUpperCase();
    const typeLabel = optionType === "CALL" ? "Call" : optionType === "PUT" ? "Put" : optionType || "";
    const strikeNum = decNum(row?.strike) || parseStrikeFromSymbol(sym);
    const strikeLabel = strikeNum > 0
        ? strikeNum.toLocaleString(undefined, { maximumFractionDigits: 3 })
        : decStr(row?.strike);

    const expiryCode = sym.match(/-(\d{6})-/i)?.[1];
    const base = sym.split("-")[0] || sym;
    if (expiryCode) {
        return {
            primary: `${base}-${expiryCode}`,
            secondary: strikeLabel && typeLabel ? `${strikeLabel}-${typeLabel}` : typeLabel || "—",
        };
    }

    return {
        primary: sym || "—",
        secondary: strikeLabel && typeLabel ? `${strikeLabel}-${typeLabel}` : "—",
    };
}

export function formatOptionsPositionExpiry(row) {
    if (row?.expiry_time) return fmtTs(row.expiry_time);
    const dateStr = parseExpiryFromSymbol(row?.symbol);
    if (dateStr) return `${dateStr} 08:00:00`;
    return "—";
}

export function formatOptionsPositionStrike(row) {
    const fromRow = decNum(row?.strike);
    const strike = fromRow > 0 ? fromRow : parseStrikeFromSymbol(row?.symbol);
    if (strike > 0) {
        return strike.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }
    return "—";
}

/** Normalized view model for open positions table rows. */
export function mapOptionsOpenPositionRowView(row, openOrders = []) {
    const sideFmt = formatOptionsPositionHistorySide(row?.side);
    const pnlFmt = formatOptionsPositionPnl(row?.unrealized_pnl, row?.pnl_percent);
    const qtyNum = decNum(row?.quantity);
    const available = computeOptionsPositionAvailable(row, openOrders);
    const marketValue = computeOptionsPositionMarketValue(row);

    return {
        key: row?.symbol || row?._id,
        expiryDate: formatOptionsPositionExpiry(row),
        symbol: row?.symbol || "—",
        symbolDisplay: formatOptionsPositionSymbolDisplay(row?.symbol, row),
        side: sideFmt,
        sideAccent: optionsPositionSideAccent(row?.side),
        amount: qtyNum > 0 ? decStr(row.quantity) : "—",
        available: available > 0 ? decStr(available) : "0",
        strikePrice: formatOptionsPositionStrike(row),
        entryPrice: decNum(row?.avg_price) > 0 ? decStr(row.avg_price) : "—",
        markPrice: decNum(row?.mark_price) > 0 ? decStr(row.mark_price) : "—",
        pnl: pnlFmt,
        marketValue: marketValue > 0 ? marketValue : null,
        raw: row,
    };
}

/** User-centric side/fee/role from GET /v1/options/trades row. */
export function resolveOptionsTradeUserView(trade, userId) {
    const uid = String(userId || "");
    const buyerId = String(trade?.buyer_id || "");
    const sellerId = String(trade?.seller_id || "");

    let side = null;
    let fee = 0;
    if (uid && buyerId && buyerId === uid) {
        side = "Buy";
        fee = decNum(trade.buyer_fee);
    } else if (uid && sellerId && sellerId === uid) {
        side = "Sell";
        fee = decNum(trade.seller_fee);
    }

    const buyerRate = decNum(trade?.buyer_fee_rate);
    const sellerRate = decNum(trade?.seller_fee_rate);
    let role = "—";
    if (side && (buyerRate > 0 || sellerRate > 0)) {
        if (buyerRate < sellerRate) {
            role = side === "Buy" ? "Maker" : "Taker";
        } else if (sellerRate < buyerRate) {
            role = side === "Sell" ? "Maker" : "Taker";
        } else {
            role = "Taker";
        }
    }

    return {
        side,
        sideClass: side === "Buy" ? "agcex-spot-side-buy" : side === "Sell" ? "agcex-spot-side-sell" : "",
        role,
        fee,
        feeAsset: trade?.fee_asset || "USDT",
    };
}

export function formatOptionsTradeId(tradeId) {
    const id = String(tradeId || "").trim();
    if (!id) return "—";
    return id.length > 6 ? `#${id.slice(-6).toUpperCase()}` : `#${id.toUpperCase()}`;
}

/** Unwrap GET /v1/options/transaction/history list. */
export function extractOptionsTransactionHistoryList(res) {
    if (!res || res.success === false) return [];
    if (Array.isArray(res.data?.transactions)) return res.data.transactions;
    if (Array.isArray(res.transactions)) return res.transactions;
    if (Array.isArray(res.data)) return res.data;
    return [];
}

export function formatOptionsTransactionType(type) {
    const s = String(type || "").toUpperCase();
    const labels = {
        TRANSFER_IN: "Transfer In",
        TRANSFER_OUT: "Transfer Out",
        TRADING_FEE: "Trading Fee",
        PREMIUM: "Premium",
        SETTLEMENT: "Settlement",
        EXERCISE: "Exercise",
        LIQUIDATION: "Liquidation",
        TRANSFER: "Transfer",
        TRADE: "Trade",
        FEE: "Fee",
    };
    return labels[s] || (s ? s.replace(/_/g, " ") : "—");
}

export function formatOptionsTransactionDirection(direction) {
    const s = String(direction || "").toUpperCase();
    if (s === "CREDIT") return { label: "Credit", className: "agcex-spot-side-buy" };
    if (s === "DEBIT") return { label: "Debit", className: "agcex-spot-side-sell" };
    return { label: direction || "—", className: "" };
}

export function formatOptionsTransactionAmount(amount, direction) {
    const n = decNum(amount);
    const dir = String(direction || "").toUpperCase();
    const isCredit = dir === "CREDIT";
    const isDebit = dir === "DEBIT";
    const className = isCredit
        ? "agcex-spot-side-buy"
        : isDebit
            ? "agcex-spot-side-sell"
            : (n >= 0 ? "agcex-spot-side-buy" : "agcex-spot-side-sell");
    const abs = Math.abs(n);
    const formatted = abs === 0 ? "0" : parseFloat(abs.toFixed(8)).toString();
    const sign = isCredit ? "+" : isDebit ? "-" : (n >= 0 ? "+" : "-");
    return { text: `${sign}${formatted}`, className };
}

export function formatOptionsTransactionBalance(row) {
    const after = row?.metadata?.balance_after ?? row?.balance_after ?? row?.balance;
    if (after == null || after === "") return "—";
    return decStr(after) || "—";
}

export function optionsTransactionRowKey(row) {
    return String(row?.id || row?._id || `${row?.transaction_type}-${row?.created_at}`);
}

/** Terminal order history — avg fill from avg_execution_price. */
export function formatOptionsOrderHistoryAvgFill(row) {
    const avg = decNum(row?.avg_execution_price);
    if (avg > 0) return decStr(row.avg_execution_price);
    return "—";
}

export function formatOptionsOrderHistorySide(side) {
    const s = String(side || "").toUpperCase();
    if (s === "BUY") return { label: "Buy", className: "agcex-spot-side-buy" };
    if (s === "SELL") return { label: "Sell", className: "agcex-spot-side-sell" };
    return { label: side || "—", className: "" };
}

/** GET /v1/options/positions/history — terminal position row. */
export function formatOptionsPositionHistorySide(side) {
    const s = String(side || "").toUpperCase();
    if (s === "LONG") return { label: "Long", className: "agcex-spot-side-buy" };
    if (s === "SHORT") return { label: "Short", className: "agcex-spot-side-sell" };
    return { label: side || "—", className: "" };
}

export function formatOptionsPositionHistoryStatus(status) {
    const s = String(status || "").toUpperCase();
    if (s === "CLOSED") return { label: "Closed", className: "" };
    if (s === "LIQUIDATED") return { label: "Liquidated", className: "agcex-spot-side-sell" };
    if (s === "EXPIRED") return { label: "Expired", className: "text-muted" };
    return { label: s || "—", className: "" };
}

export function formatOptionsPositionHistoryPnl(pnl) {
    const raw = decStr(pnl);
    const n = decNum(pnl);
    const className = optionsPositionPnlClass(pnl);
    if (!raw) return { text: "—", className: "" };
    if (n === 0) return { text: "0 USDT", className: "" };
    const body = raw.replace(/^[-+]/, "");
    const sign = n > 0 ? "+" : "-";
    return { text: `${sign}${body} USDT`, className };
}

export function formatOptionsPositionHistoryId(historyId, positionId) {
    const id = String(historyId || positionId || "").trim();
    if (!id) return "—";
    return id.length > 6 ? `#${id.slice(-6).toUpperCase()}` : `#${id.toUpperCase()}`;
}

export function optionsPositionHistoryRowKey(row) {
    if (row?.history_id) return String(row.history_id);
    if (row?.position_id) return String(row.position_id);
    return `${row?.symbol || "row"}-${row?.closed_at || ""}`;
}

/** Legacy rows: quantity "0" when size was not snapshotted at close. */
export function isLegacyOptionsPositionHistoryRow(row) {
    return row?.quantity != null && decNum(row.quantity) === 0;
}

/** Unwrap GET /v1/options/positions/history list from varied response envelopes. */
export function extractOptionsPositionHistoryList(res) {
    if (!res || res.success === false) return [];
    if (Array.isArray(res.data?.positions)) return res.data.positions;
    if (Array.isArray(res.positions)) return res.positions;
    if (Array.isArray(res.data)) return res.data;
    return [];
}

/** Normalized view model for position history table rows. */
export function mapOptionsPositionHistoryRowView(row) {
    const legacy = isLegacyOptionsPositionHistoryRow(row);
    const sideFmt = formatOptionsPositionHistorySide(row?.side);
    const statusFmt = formatOptionsPositionHistoryStatus(row?.status);
    const pnlFmt = formatOptionsPositionHistoryPnl(row?.realized_pnl);
    const qtyNum = decNum(row?.quantity);
    const avgNum = decNum(row?.avg_price);
    const notionalNum = decNum(row?.open_notional);
    const optionType = String(row?.option_type || "").toUpperCase();
    const typeLabel = optionType === "CALL" ? "Call" : optionType === "PUT" ? "Put" : optionType || null;

    return {
        key: optionsPositionHistoryRowKey(row),
        closedAt: fmtTs(row?.closed_at),
        expiryDate: row?.expiry_time ? fmtTs(row.expiry_time) : formatOptionsPositionExpiry(row),
        symbol: row?.symbol || "—",
        symbolDisplay: formatOptionsPositionSymbolDisplay(row?.symbol, row),
        side: sideFmt,
        sideAccent: optionsPositionSideAccent(row?.side),
        amount: qtyNum > 0 ? decStr(row.quantity) : legacy ? "—" : decStr(row.quantity) || "—",
        size: qtyNum > 0 ? decStr(row.quantity) : legacy ? "—" : decStr(row.quantity) || "—",
        sizeTitle: legacy ? "Size not recorded for legacy position" : undefined,
        strikePrice: formatOptionsPositionStrike(row),
        avgPrice: avgNum > 0 ? decStr(row.avg_price) : "—",
        openNotional: notionalNum > 0 ? `${decStr(row.open_notional)} USDT` : "—",
        pnl: pnlFmt,
        status: statusFmt,
        id: formatOptionsPositionHistoryId(row?.history_id, row?.position_id),
        idTitle: row?.history_id || row?.position_id || "",
        optionType: typeLabel,
        strike: decStr(row?.strike) || null,
        expiryTime: row?.expiry_time ? fmtTs(row.expiry_time) : null,
        legacy,
    };
}

/** USDT display for options account panel (4 decimals). */
export function formatOptionsAccountUsdt(v) {
    const n = decNum(v);
    if (!Number.isFinite(n)) return "0.0000 USDT";
    return `${n.toFixed(4)} USDT`;
}

export function formatOptionsMarginRatioFromRatio(ratio) {
    const n = decNum(ratio);
    if (!Number.isFinite(n) || n <= 0) return "0.00%";
    return `${(n * 100).toFixed(2)}%`;
}

export function computeOptionsAccountMarginRatioPct(accountUpdate) {
    const equity = decNum(accountUpdate?.total_equity);
    const maint = decNum(accountUpdate?.maintenance_margin);
    if (maint <= 0) return "0.00%";
    return `${((equity / maint) * 100).toFixed(2)}%`;
}

/** Position multiplier when shorts exist; otherwise equity / maintenance × 100. */
export function resolveOptionsAccountMarginRatioValue(accountUpdate, positions) {
    const shorts = (positions || []).filter(
        (p) => String(p.side || "").toUpperCase() === "SHORT" && p.margin_ratio != null
    );
    if (shorts.length) {
        const worst = shorts.reduce((a, b) =>
            (decNum(a.margin_ratio) < decNum(b.margin_ratio) ? a : b)
        );
        return { kind: "position", value: decNum(worst.margin_ratio) };
    }
    const equity = decNum(accountUpdate?.total_equity);
    const maint = decNum(accountUpdate?.maintenance_margin);
    if (maint <= 0) return { kind: "account", value: 0 };
    return { kind: "account", value: (equity / maint) * 100 };
}

export function formatOptionsAccountMarginRatioDisplay(accountUpdate, positions) {
    const { kind, value } = resolveOptionsAccountMarginRatioValue(accountUpdate, positions);
    if (kind === "position") return formatOptionsMarginRatioFromRatio(value);
    if (value <= 0) return "0.00%";
    return `${value.toFixed(2)}%`;
}

export function optionsAccountMarginRatioClass(accountUpdate, positions) {
    const { kind, value } = resolveOptionsAccountMarginRatioValue(accountUpdate, positions);
    if (kind === "position") {
        if (value <= 1) return "text-danger";
        if (value <= 1.5) return "text-warning";
        return "text-success";
    }
    if (value < 100) return "text-danger";
    if (value < 150) return "text-warning";
    return "text-success";
}

export function optionsAccountPnlClass(v) {
    const n = decNum(v);
    if (!Number.isFinite(n) || n === 0) return "";
    return n > 0 ? "text-success" : "text-danger";
}

/** Sum per-position unrealized PnL from `user_positions_update`. */
export function sumOptionsPositionsUnrealizedPnl(positions) {
    if (!Array.isArray(positions)) return null;
    if (positions.length === 0) return 0;
    let sum = 0;
    for (const p of positions) {
        sum += decNum(p?.unrealized_pnl);
    }
    return sum;
}

/**
 * Account unrealized PnL — prefer Σ positions when positions snapshot is ready
 * so the account panel matches the open-positions table.
 */
export function resolveOptionsAccountUnrealizedPnl(accountUpdate, positions, positionsReady = false) {
    if (positionsReady && Array.isArray(positions)) {
        return sumOptionsPositionsUnrealizedPnl(positions);
    }
    return decNum(accountUpdate?.unrealized_pnl);
}

/** Short writer margin + long position maintenance (15% notional proxy). */
export function calcOptionsInitialMargin(account, positions) {
    const shortInitial = decNum(account?.writer_margin_locked);
    const longInitial = (positions || [])
        .filter((p) => String(p.side || "").toUpperCase() === "LONG" && decNum(p.quantity) > 0)
        .reduce((sum, p) => sum + decNum(p.maintenance_margin), 0);
    return shortInitial + longInitial;
}

/** Requirement-style % (margin ÷ equity) — IMR / MMR labels. */
export function formatOptionsMarginUsagePct(margin, equity) {
    const m = decNum(margin);
    const e = decNum(equity);
    if (e <= 0) return "—";
    if (m <= 0) return "0.00%";
    return `${((m / e) * 100).toFixed(2)}%`;
}

export function optionsMarginUsagePctValue(margin, equity) {
    const m = decNum(margin);
    const e = decNum(equity);
    if (e <= 0 || m <= 0) return 0;
    return (m / e) * 100;
}

export function optionsMarginUsageClass(pct) {
    const n = decNum(pct);
    if (n >= 100) return "text-danger";
    if (n >= 80) return "";
    return "text-green";
}

export function calcOptionsAccountImrMmr(account, positions) {
    const equity = decNum(account?.total_equity);
    const maintenance = decNum(account?.maintenance_margin);
    const initial = calcOptionsInitialMargin(account, positions);
    const imrPct = optionsMarginUsagePctValue(initial, equity);
    const mmrPct = optionsMarginUsagePctValue(maintenance, equity);
    return {
        imr: formatOptionsMarginUsagePct(initial, equity),
        mmr: formatOptionsMarginUsagePct(maintenance, equity),
        imrPct,
        mmrPct,
        imrClass: equity <= 0 ? "" : optionsMarginUsageClass(imrPct),
        mmrClass: equity <= 0 ? "" : optionsMarginUsageClass(mmrPct),
        imrProgress: Math.min(100, imrPct),
        mmrProgress: Math.min(100, mmrPct),
    };
}
